export type AIModelCategory = 'text' | 'image' | 'video' | 'voice' | 'code';

export interface AIModel {
  id: string;
  name: string;
  brand: string;
  category: AIModelCategory;
  color: string;
  icon?: string;
}

/**
 * Bundled fallback list. The live source of truth is now the backend
 * `ai_models` table — `AIModelsProvider` (in `lib/ai-models-context.tsx`)
 * fetches the live list on mount and exposes `useAIModels()` /
 * `useGetAIModel()` for components.
 *
 * This array stays as the **bundle-time fallback** so:
 *   1. The first paint has a non-empty model list (no flash of empty chips)
 *   2. The lookup map below still resolves slugs even before the fetch
 *      resolves — preventing chips on already-rendered prompts from showing
 *      raw slugs like `openai/gpt-4o` for a frame.
 *
 * If you add a new model, prefer doing it from the admin UI instead of
 * editing this list — the runtime registry is what users will see.
 */
export const AI_MODELS: AIModel[] = [
  // === Text / Chat ===
  { id: 'openai/gpt-4o',            name: 'GPT-4o',              brand: 'OpenAI',            category: 'text',  color: '#10a37f' },
  { id: 'openai/gpt-4-turbo',       name: 'GPT-4 Turbo',         brand: 'OpenAI',            category: 'text',  color: '#10a37f' },
  { id: 'openai/o1',                name: 'o1',                  brand: 'OpenAI',            category: 'text',  color: '#10a37f' },
  { id: 'openai/o3-mini',           name: 'o3-mini',             brand: 'OpenAI',            category: 'text',  color: '#10a37f' },
  { id: 'anthropic/claude-3.7-sonnet', name: 'Claude 3.7 Sonnet', brand: 'Anthropic',        category: 'text',  color: '#d97757' },
  { id: 'anthropic/claude-3.5-sonnet', name: 'Claude 3.5 Sonnet', brand: 'Anthropic',        category: 'text',  color: '#d97757' },
  { id: 'anthropic/claude-3-opus',  name: 'Claude 3 Opus',       brand: 'Anthropic',         category: 'text',  color: '#d97757' },
  { id: 'anthropic/claude-3-haiku', name: 'Claude 3 Haiku',      brand: 'Anthropic',         category: 'text',  color: '#d97757' },
  { id: 'google/gemini-2.0-flash',  name: 'Gemini 2.0 Flash',    brand: 'Google',            category: 'text',  color: '#4285f4' },
  { id: 'google/gemini-1.5-pro',    name: 'Gemini 1.5 Pro',      brand: 'Google',            category: 'text',  color: '#4285f4' },
  { id: 'meta/llama-3.3',           name: 'Llama 3.3',           brand: 'Meta',              category: 'text',  color: '#0081fb' },
  { id: 'xai/grok-3',               name: 'Grok 3',              brand: 'xAI',               category: 'text',  color: '#1a1a1a' },
  { id: 'deepseek/v3',              name: 'DeepSeek V3',         brand: 'DeepSeek',          category: 'text',  color: '#4d6bfe' },
  { id: 'deepseek/r1',              name: 'DeepSeek R1',         brand: 'DeepSeek',          category: 'text',  color: '#4d6bfe' },
  { id: 'mistral/large',            name: 'Mistral Large',       brand: 'Mistral',           category: 'text',  color: '#ff7000' },

  // === Image ===
  { id: 'openai/dall-e-3',          name: 'DALL-E 3',            brand: 'OpenAI',            category: 'image', color: '#10a37f' },
  { id: 'midjourney/v6',            name: 'Midjourney v6',       brand: 'Midjourney',        category: 'image', color: '#0095ff' },
  { id: 'midjourney/v7',            name: 'Midjourney v7',       brand: 'Midjourney',        category: 'image', color: '#0095ff' },
  { id: 'stability/sdxl',           name: 'Stable Diffusion XL', brand: 'Stability',         category: 'image', color: '#9b59b6' },
  { id: 'stability/sd3',            name: 'Stable Diffusion 3',  brand: 'Stability',         category: 'image', color: '#9b59b6' },
  { id: 'bfl/flux-dev',             name: 'Flux Dev',            brand: 'Black Forest Labs', category: 'image', color: '#1a1a1a' },
  { id: 'bfl/flux-pro',             name: 'Flux Pro',            brand: 'Black Forest Labs', category: 'image', color: '#1a1a1a' },
  { id: 'google/imagen-3',          name: 'Imagen 3',            brand: 'Google',            category: 'image', color: '#4285f4' },
  { id: 'ideogram/v2',              name: 'Ideogram 2',          brand: 'Ideogram',          category: 'image', color: '#6366f1' },

  // === Video ===
  { id: 'openai/sora',              name: 'Sora',                brand: 'OpenAI',            category: 'video', color: '#10a37f' },
  { id: 'runway/gen-3',             name: 'Runway Gen-3',        brand: 'Runway',            category: 'video', color: '#1a1a1a' },
  { id: 'kling/v1',                 name: 'Kling',               brand: 'Kling',             category: 'video', color: '#ff4081' },
  { id: 'google/veo-2',             name: 'Veo 2',               brand: 'Google',            category: 'video', color: '#4285f4' },
  { id: 'luma/dream-machine',       name: 'Luma Dream',          brand: 'Luma',              category: 'video', color: '#7c3aed' },

  // === Voice / Audio ===
  { id: 'elevenlabs/multilingual',  name: 'ElevenLabs',          brand: 'ElevenLabs',        category: 'voice', color: '#000000' },
  { id: 'suno/v4',                  name: 'Suno v4',             brand: 'Suno',              category: 'voice', color: '#ea580c' },
  { id: 'udio',                     name: 'Udio',                brand: 'Udio',              category: 'voice', color: '#7c3aed' },

  // === Code / Agents ===
  { id: 'anthropic/claude-code',    name: 'Claude Code',         brand: 'Anthropic',         category: 'code',  color: '#d97757' },
  { id: 'anysphere/cursor',         name: 'Cursor',              brand: 'Cursor',            category: 'code',  color: '#1a1a1a' },
  { id: 'github/copilot',           name: 'GitHub Copilot',      brand: 'GitHub',            category: 'code',  color: '#181717' },
];

export const AI_MODEL_CATEGORIES: { key: AIModelCategory; label: string; icon: string }[] = [
  { key: 'text',  label: 'Text & Chat',      icon: 'chat' },
  { key: 'image', label: 'Image generation', icon: 'image' },
  { key: 'video', label: 'Video',            icon: 'movie' },
  { key: 'voice', label: 'Voice & Audio',    icon: 'graphic_eq' },
  { key: 'code',  label: 'Code & Agents',    icon: 'code' },
];

// Mutable lookup map — kept here so the legacy `getAIModel(id)` call sites
// (PromptCard, PromptDetailModal, create/edit pages) stay working without
// having to wire every one through the context. `AIModelsProvider` calls
// `replaceAIModelsRegistry(...)` once the live fetch resolves.
let BY_ID = new Map(AI_MODELS.map((m) => [m.id, m]));

export function replaceAIModelsRegistry(rows: AIModel[]) {
  // Build the new map first so a partially-filled map can never be observed.
  BY_ID = new Map(rows.map((m) => [m.id, m]));
}

export function getAIModel(id: string): AIModel | undefined {
  return BY_ID.get(id);
}

export function displayModelName(id: string): string {
  return BY_ID.get(id)?.name ?? id;
}

/** Snapshot of the current registry — components that need the FULL list
 * (e.g. the model picker on `/create`) should call this. Returns the
 * bundled defaults until the live fetch completes. */
export function listAIModels(): AIModel[] {
  return Array.from(BY_ID.values());
}
